/* ***************************************************************************
 * attachmentBudget — CON-1925
 *
 * One outbound MMS has a TOTAL size budget (state.account.mmsAttachmentPolicy
 * .totalBytes, 300 KB by default), not a per-file one. With several files the
 * client has to decide, before anything is uploaded, how small each new image
 * must be compressed so the whole message still fits. This module is that
 * decision, kept pure so it can be tested without a browser.
 *
 * The rules, in order:
 *   1. Files already on the message count at their stored size. The contact
 *      card is generated per send and counts as 0.
 *   2. New files that cannot be compressed (pdf, vcf, audio, video, docs)
 *      count at their actual size.
 *   3. Whatever budget is left is split evenly across the new compressible
 *      images; each is compressed to its share. If a share would fall below
 *      MIN_IMAGE_TARGET_BYTES the picture would be unusable, so the pick is
 *      refused and the user is told to remove something.
 *
 * The server checks the same total on create (attachmentPolicy) and again at
 * send (multiAttachmentSend), so this is the first guard, not the only one.
 *
 * The copy constants are quoted in the Knowledge Base; change them there too.
 *************************************************************************** */

import { isCompressibleImage } from 'src/utils/imageCompression';

/* CON-1936 — whether a picked file may be shrunk to share the budget. A GIF
 * may not: shrinking redraws it on a canvas, which keeps only its first frame
 * (an animated GIF arrived as a still picture). It counts at its own size
 * instead, so one too large for the budget is refused. The single-file path
 * outside the beta is unchanged (it does not use this). */
export const isGifFile = (file) => !!file
  && (String(file.type || '').toLowerCase() === 'image/gif' || /\.gif$/i.test(String(file.name || '')));
export const isBudgetCompressible = (file) => isCompressibleImage(file) && !isGifFile(file);

export const MIN_IMAGE_TARGET_BYTES = 60 * 1024;

export const MIXED_CONTAINER_MESSAGE = 'An attachment from a message template can\'t be combined with uploaded files. Remove it, or send it on its own.';

/* CON-1930 — a pick or paste while the last one is still being compressed
 * and uploaded is turned away with this, rather than running two rebalances
 * over the same files at once. Quoted in the Knowledge Base. */
export const ATTACHMENTS_BUSY_MESSAGE = 'We\'re still adding your last file. Try again in a moment.';

export const tooManyFilesMessage = (maxAttachments) => (
  `You can attach up to ${maxAttachments} files to one text.`
);

export const overBudgetMessage = (totalBytes) => (
  `Together, attachments must stay under ${Math.round(totalBytes / 1024)} KB. Remove one or choose a smaller file.`
);

const toKb = (bytes) => Math.round(bytes / 1024);

/* ***************************************************************************
 * planNewFileTargets
 *
 * @param {File[]} newFiles         files just picked, in order
 * @param {number} existingBytes    sum of the files already on the message
 * @param {number} totalBytes       the channel budget
 * @returns {{ ok: boolean, targets: (number|null)[], error: string|null,
 *             fixedBytes: number, remainingBytes: number }}
 *   targets[i] is the compression target for newFiles[i], or null for a
 *   file that is not compressed (it is counted at its own size instead).
 *************************************************************************** */
export const planNewFileTargets = ({
  newFiles = [],
  existingBytes = 0,
  totalBytes,
  minImageBytes = MIN_IMAGE_TARGET_BYTES,
}) => {
  const budget = Number(totalBytes) > 0 ? Number(totalBytes) : 0;
  const files = Array.isArray(newFiles) ? newFiles : [];
  const compressible = files.filter((file) => isBudgetCompressible(file));
  const fixed = files.filter((file) => !isBudgetCompressible(file));

  const fixedBytes = fixed.reduce((sum, file) => sum + (file?.size || 0), 0)
    + (Number(existingBytes) > 0 ? Number(existingBytes) : 0);
  const remainingBytes = budget - fixedBytes;

  if (remainingBytes < 0) {
    return {
      ok: false,
      targets: [],
      error: overBudgetMessage(budget),
      fixedBytes,
      remainingBytes,
    };
  }

  let perImage = null;
  if (compressible.length) {
    perImage = Math.floor(remainingBytes / compressible.length);
    if (perImage < minImageBytes) {
      return {
        ok: false,
        targets: [],
        error: `${overBudgetMessage(budget)} (${toKb(remainingBytes)} KB is left for ${compressible.length} image${compressible.length === 1 ? '' : 's'}.)`,
        fixedBytes,
        remainingBytes,
      };
    }
  }

  return {
    ok: true,
    targets: files.map((file) => (isBudgetCompressible(file) ? perImage : null)),
    error: null,
    fixedBytes,
    remainingBytes,
  };
};

/* ***************************************************************************
 * planRebalance — CON-1929
 *
 * The whole set, not just the new files. Adding images one at a time used to
 * compress each new image against whatever budget the earlier ones had left,
 * so two screenshots could leave 21 KB for a third. Now every add re-plans
 * every attachment on the message:
 *
 *   1. Files that cannot be compressed (pdf, vcf, audio, video, docs) count
 *      at their size, as before.
 *   2. The rest of the budget is shared across the images by water-filling:
 *      smallest first, an image that already fits its share keeps its size
 *      and the room it leaves goes to the bigger ones; an image over its
 *      share gets that share as its compression target.
 *   3. If an image would have to go below MIN_IMAGE_TARGET_BYTES the add is
 *      refused, with the same message as before.
 *
 * @param {{ size: number, compressible: boolean }[]} items  every attachment,
 *        existing and new, in message order
 * @returns {{ ok, targets: (number|null)[], error }}  targets[i] is the
 *        compression target for items[i], or null to keep it as it is.
 *************************************************************************** */
export const planRebalance = ({
  items = [],
  totalBytes,
  minImageBytes = MIN_IMAGE_TARGET_BYTES,
}) => {
  const budget = Number(totalBytes) > 0 ? Number(totalBytes) : 0;
  const list = Array.isArray(items) ? items : [];
  const targets = list.map(() => null);

  const fixedBytes = list
    .filter((item) => !item.compressible)
    .reduce((sum, item) => sum + (Number(item.size) > 0 ? Number(item.size) : 0), 0);
  let remaining = budget - fixedBytes;
  if (remaining < 0) {
    return { ok: false, targets: [], error: overBudgetMessage(budget) };
  }

  const images = list
    .map((item, index) => ({ index, size: Number(item.size) > 0 ? Number(item.size) : 0 }))
    .filter(({ index }) => list[index].compressible)
    .sort((a, b) => a.size - b.size);

  for (let i = 0; i < images.length; i++) {
    const image = images[i];
    const left = images.length - i;
    const share = Math.floor(remaining / left);
    if (image.size <= share) {
      remaining -= image.size;
    } else {
      if (share < minImageBytes) {
        return {
          ok: false,
          targets: [],
          error: `${overBudgetMessage(budget)} (${toKb(remaining)} KB is left for ${left} image${left === 1 ? '' : 's'}.)`,
        };
      }
      targets[image.index] = share;
      remaining -= share;
    }
  }

  return { ok: true, targets, error: null };
};

/* ***************************************************************************
 * fetchBlobSizeBytes
 * HEAD a blob URL (with SAS) and read Content-Length. 0 on any failure: the
 * server re-checks the real total, so the client fails open here rather than
 * blocking a send over a transient storage error.
 *************************************************************************** */
export const fetchBlobSizeBytes = async (url) => {
  if (!url) return 0;
  try {
    const response = await fetch(url, { method: 'HEAD' });
    const length = response?.headers?.get?.('content-length');
    const bytes = Number(length);
    return Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  } catch (e) {
    return 0;
  }
};

/* ***************************************************************************
 * sumAttachmentBytes
 * The stored size of every blob name given, through the URL builder the
 * caller passes (so this file stays free of the Azure helpers). CON-1934 —
 * the contact card counts at its real size (about 70 KB with a photo); the
 * caller's builder must read it from the user's own container
 * (contactCardAttachments.containerForFile).
 *************************************************************************** */
export const sumAttachmentBytes = async (blobNames, buildUrl) => {
  const names = (Array.isArray(blobNames) ? blobNames : []).filter(Boolean);
  const sizes = await Promise.all(names.map((name) => fetchBlobSizeBytes(buildUrl(name))));
  return sizes.reduce((sum, bytes) => sum + bytes, 0);
};
